import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { z } from "zod";
import { Alert, Button, Group, Modal, Stack, Text, Textarea, TextInput } from "@mantine/core";
import { IconExclamationCircle } from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import { useCallback, useMemo, useState } from "react";
import { getInitSpec, getInitStyle } from "../reducers/initReducer.js";
import { useAppAction, useAppSelector } from "../hooks/redux.js";
import { getCurrentSpec } from "../utils/selectors.js";
import { isEqual } from "es-toolkit";
import { initStyle } from "../reducers/historyEnhancer.js";
import { FormattedMessage, useIntl } from "react-intl";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { redirectToStyle } from "../utils/redirect.js";
import { useCaptureEvent } from "./StyleEditorContext.js";
import { Event } from "../constants/events.js";
export const CreateMapModal = ({ opened, onClose, onCreate }) => {
    const intl = useIntl();
    const style = useAppSelector(getInitStyle);
    const initSpec = useAppSelector(getInitSpec);
    const currentSpec = useAppSelector(getCurrentSpec);
    const setInitState = useAppAction(initStyle);
    const captureEvent = useCaptureEvent();
    const [createError, setError] = useState(null);
    const [isPending, setIsPending] = useState(false);
    const validate = useMemo(() => zod4Resolver(z.object({
        displayName: z
            .string()
            .min(1, {
            message: intl.formatMessage({
                defaultMessage: "Map name is required",
                id: "error.mapNameRequired",
            }),
        })
            .max(255, {
            message: intl.formatMessage({
                defaultMessage: "Map name is too long",
                id: "error.mapNameTooLong",
            }),
        }),
        description: z.string().optional(),
        sourceStyleId: z.number(),
    })), [intl]);
    const { onSubmit, getInputProps } = useForm({
        validateInputOnBlur: true,
        initialValues: {
            displayName: "",
            description: "",
            sourceStyleId: style.id,
        },
        validate,
    });
    const handleCreate = useCallback(({ displayName, description, sourceStyleId }) => {
        if (!onCreate)
            return;
        const spec = !isEqual(currentSpec, initSpec) ? currentSpec : undefined;
        setIsPending(true);
        setError(null);
        void onCreate({
            displayName,
            description,
            sourceStyleId,
            spec,
        })
            .then((newStyle) => {
            setInitState(newStyle);
            redirectToStyle(newStyle);
            captureEvent(Event.CreateMapSuccess);
            onClose();
        })
            .catch((error) => {
            setError(error);
        })
            .finally(() => {
            setIsPending(false);
        });
    }, [initSpec, currentSpec, setInitState, onClose, onCreate]);
    let errorMessage = null;
    if (createError) {
        if (createError.message.includes("duplicate key value")) {
            errorMessage = intl.formatMessage({
                defaultMessage: "Map with this name already exists",
                id: "error.mapNameDuplicate",
            });
        }
        else {
            errorMessage = createError.message;
        }
    }
    return (_jsx(Modal, { opened: opened, onClose: onClose, title: intl.formatMessage({ defaultMessage: "Create map", id: "title.createMap" }), size: "55rem", children: _jsxs(Stack, { children: [createError && (_jsx(Alert, { color: "red", icon: _jsx(IconExclamationCircle, {}), children: errorMessage })), _jsx(Text, { children: _jsx(FormattedMessage, { id: "text.createMapDescription", defaultMessage: "This map need to be saved in your current account. Name your map and save changes." }) }), _jsx("form", { onSubmit: onSubmit(handleCreate), children: _jsxs(Stack, { gap: "md", children: [_jsx(TextInput, { label: intl.formatMessage({
                                    defaultMessage: "Name",
                                    id: "label.name",
                                }), placeholder: intl.formatMessage({
                                    defaultMessage: "My awesome map",
                                    id: "placeholder.mapDisplayName",
                                }), required: true, ...getInputProps("displayName") }), _jsx(Textarea, { label: intl.formatMessage({
                                    defaultMessage: "Description",
                                    id: "label.description",
                                }), placeholder: intl.formatMessage({
                                    defaultMessage: "Enter map description...",
                                    id: "placeholder.mapDescription",
                                }), ...getInputProps("description") }), _jsxs(Group, { gap: "md", justify: "flex-end", mt: "md", children: [_jsx(Button, { variant: "default", onClick: onClose, disabled: isPending, children: _jsx(FormattedMessage, { id: "button.cancel", defaultMessage: "Cancel" }) }), _jsx(Button, { variant: "tertiary", type: "submit", loading: isPending, children: _jsx(FormattedMessage, { id: "button.create", defaultMessage: "Create" }) })] })] }) })] }) }));
};
